/**
 * Reports → Settings (`/reports/settings`; #1115 "Your calls"): KPI targets, whether techs see their own Team numbers,
 * and two metric definitions. One app setting, `reports.settings`, read from and saved through the admin settings API
 * (`GET /admin/settings`, `PUT /admin/settings/reports.settings`), manager only. The API validates the whole value and
 * answers 422 in words, which this screen shows as is. Owner: slice rs-web.
 */
import { Button, EmptyState, ErrorBanner, Input, Section, SkeletonRows, Switch, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { Frame, PaneHeader } from '../../components/frame'
import type { SettingOut, TargetableMetric } from '../../lib/api/generated'
import { api } from '../admin/api'
import { MutationError } from '../admin/presentation'
import { settingsQuery } from '../admin/queries'
import { useMe } from '../auth/session'
import { catalogueQuery } from './api'
import { formatMinutes, toNum } from './charts/format'
import { Breadcrumb } from './report'
import { QueryError } from './shared'

export const SETTINGS_KEY = 'reports.settings'
export const DEFAULT_GRACE = 15

/** The API's `ReportsSettings` (apps/api/app/reports/suite/settings.py). Targets arrive as decimal strings. */
export interface ReportsSettingsValue {
  targets: Record<string, number>
  techs_see_own_numbers: boolean
  fcr_counts_onsite: boolean
  arrival_grace_minutes: number
}

/** The form: inputs as typed text, so a half-typed number is never coerced. Blank target = no target. */
interface FormState {
  targets: Record<string, string>
  techs_see_own_numbers: boolean
  fcr_counts_onsite: boolean
  arrival_grace_minutes: string
}

/** Reads the stored value defensively: anything malformed falls back to the model's default for that field. */
export function readSettings(value: unknown): ReportsSettingsValue {
  const v = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  const targets: Record<string, number> = {}
  if (v.targets && typeof v.targets === 'object') {
    for (const [key, raw] of Object.entries(v.targets as Record<string, unknown>)) {
      const n = toNum(raw as string | number | null)
      if (n !== null) targets[key] = n
    }
  }
  const grace = toNum(v.arrival_grace_minutes as string | number | null)
  return {
    targets,
    techs_see_own_numbers: v.techs_see_own_numbers === true,
    fcr_counts_onsite: v.fcr_counts_onsite === true,
    arrival_grace_minutes: grace ?? DEFAULT_GRACE,
  }
}

function toForm(value: ReportsSettingsValue): FormState {
  return {
    targets: Object.fromEntries(Object.entries(value.targets).map(([k, n]) => [k, String(n)])),
    techs_see_own_numbers: value.techs_see_own_numbers,
    fcr_counts_onsite: value.fcr_counts_onsite,
    arrival_grace_minutes: String(value.arrival_grace_minutes),
  }
}

const UNIT_SUFFIX: Record<TargetableMetric['unit'], string> = {
  minutes: 'min',
  hours: 'h',
  days: 'd',
  percent: '%',
  count: '',
}

/** Why a target input is refused, or null. */
export function targetProblem(unit: TargetableMetric['unit'], text: string): string | null {
  if (text.trim() === '') return null
  const n = Number(text)
  if (!Number.isFinite(n)) return 'Enter a number, or leave it blank for no target.'
  if (n < 0) return 'A target is zero or more.'
  if (unit === 'percent' && n > 100) return 'A percentage is 0 to 100.'
  if (unit === 'count' && !Number.isInteger(n)) return 'A count is a whole number.'
  return null
}

export function graceProblem(text: string): string | null {
  const n = Number(text)
  if (text.trim() === '' || !Number.isInteger(n)) return 'Enter whole minutes.'
  if (n < 0 || n > 120) return 'Grace is 0 to 120 minutes.'
  return null
}

/**
 * The PUT body. Targets the screen does not list (a key the catalogue no longer offers) are kept as stored, so saving
 * this form never silently drops one; the API refuses a key it does not know, in words.
 */
export function buildPayload(stored: ReportsSettingsValue, form: FormState, listed: TargetableMetric[]): ReportsSettingsValue {
  const targets: Record<string, number> = { ...stored.targets }
  for (const m of listed) {
    const text = form.targets[m.key]?.trim() ?? ''
    if (text === '') delete targets[m.key]
    else targets[m.key] = Number(text)
  }
  return {
    targets,
    techs_see_own_numbers: form.techs_see_own_numbers,
    fcr_counts_onsite: form.fcr_counts_onsite,
    arrival_grace_minutes: Number(form.arrival_grace_minutes),
  }
}

const sameValue = (a: ReportsSettingsValue, b: ReportsSettingsValue) => {
  const keys = new Set([...Object.keys(a.targets), ...Object.keys(b.targets)])
  for (const k of keys) if (a.targets[k] !== b.targets[k]) return false
  return (
    a.techs_see_own_numbers === b.techs_see_own_numbers &&
    a.fcr_counts_onsite === b.fcr_counts_onsite &&
    a.arrival_grace_minutes === b.arrival_grace_minutes
  )
}

function TargetRow({ metric, text, onChange }: { metric: TargetableMetric; text: string; onChange: (text: string) => void }) {
  const id = React.useId()
  const problem = targetProblem(metric.unit, text)
  const n = text.trim() === '' || problem ? null : Number(text)
  // Minutes are entered in minutes; the hint shows them the way the tiles will ("3.4 h").
  const hint = metric.unit === 'minutes' && n !== null && n >= 90 ? `= ${formatMinutes(n)}` : null
  const suffix = UNIT_SUFFIX[metric.unit]
  return (
    <li data-target-row={metric.key} className="grid grid-cols-1 gap-x-4 gap-y-1 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium">
          {metric.label}
        </label>
        <p className="text-xs text-muted-foreground">
          {metric.good === 'down' ? 'Good when at most the target' : 'Good when at least the target'}
        </p>
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-14 text-right text-xs text-muted-foreground">{metric.good === 'down' ? 'at most' : 'at least'}</span>
          <Input
            id={id}
            inputMode="decimal"
            className="h-8 w-28 tabular"
            placeholder="No target"
            value={text}
            aria-invalid={Boolean(problem)}
            aria-describedby={problem ? `${id}-problem` : undefined}
            onChange={(e) => onChange(e.currentTarget.value)}
          />
          <span className="w-8 text-xs text-muted-foreground">{suffix}</span>
        </div>
        {problem ? (
          <p id={`${id}-problem`} className="text-xs text-destructive sm:pl-16">
            {problem}
          </p>
        ) : hint ? (
          <p className="tabular text-xs text-muted-foreground sm:pl-16">{hint}</p>
        ) : null}
      </div>
    </li>
  )
}

export function SettingsForm({ row, targetable }: { row: SettingOut; targetable: TargetableMetric[] }) {
  const me = useMe()
  const queryClient = useQueryClient()
  // Keyed on the stored JSON, so a background refetch of the same value never wipes an edit in progress.
  const storedJson = JSON.stringify(row.value ?? null)
  const stored = React.useMemo(() => readSettings(JSON.parse(storedJson)), [storedJson])
  const [form, setForm] = React.useState<FormState>(() => toForm(stored))
  React.useEffect(() => setForm(toForm(stored)), [stored])
  const graceId = React.useId()

  const problems = [
    ...targetable.map((m) => targetProblem(m.unit, form.targets[m.key] ?? '')),
    graceProblem(form.arrival_grace_minutes),
  ].filter(Boolean)
  const payload = buildPayload(stored, form, targetable)
  const dirty = !sameValue(payload, stored)

  const save = useMutation({
    mutationFn: () => api.putSetting(SETTINGS_KEY, payload, me?.csrf_token ?? ''),
    onSuccess: async () => {
      // Targets and definitions change what every report and tile shows: re-read the catalogue and every report.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: settingsQuery().queryKey }),
        queryClient.invalidateQueries({ queryKey: ['reports-suite'] }),
      ])
      toast.success('Report settings saved')
    },
  })
  const set = (patch: Partial<FormState>) => {
    if (save.isError) save.reset()
    setForm((prev) => ({ ...prev, ...patch }))
  }
  const graceError = graceProblem(form.arrival_grace_minutes)

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (dirty && !problems.length && !save.isPending) save.mutate()
      }}
    >
      {row.stored_valid === false ? (
        <ErrorBanner
          tone="warning"
          title="The saved report settings no longer validate"
          detail="The defaults are in force until you save again."
        />
      ) : null}
      <Section title="KPI targets" bodyClassName="space-y-2">
        <p className="text-sm text-muted-foreground">
          A target shows on every tile for that figure, with met or missed. Leave a row blank for no target. Money and cost figures never
          take one.
        </p>
        {targetable.length ? (
          <ul className="divide-y divide-border">
            {targetable.map((m) => (
              <TargetRow
                key={m.key}
                metric={m}
                text={form.targets[m.key] ?? ''}
                onChange={(text) => set({ targets: { ...form.targets, [m.key]: text } })}
              />
            ))}
          </ul>
        ) : (
          <EmptyState title="No figures take a target yet." />
        )}
      </Section>
      <Section title="Who sees what" bodyClassName="space-y-1">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Switch
            aria-label="Techs see their own numbers on the Team report"
            checked={form.techs_see_own_numbers}
            onCheckedChange={(on) => set({ techs_see_own_numbers: on })}
          />
          Techs see their own numbers on the Team report
        </label>
        <p className="text-xs text-muted-foreground">
          A tech opens Team as "My numbers": their own row only, never anyone else's. Off means only managers and cover see Team.
        </p>
      </Section>
      <Section title="Definitions" bodyClassName="space-y-4">
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Switch
              aria-label="First-contact resolution includes a case fixed in one onsite visit"
              checked={form.fcr_counts_onsite}
              onCheckedChange={(on) => set({ fcr_counts_onsite: on })}
            />
            First-contact resolution includes a case fixed in one onsite visit
          </label>
          <p className="text-xs text-muted-foreground">
            Off counts only a case settled by one sent reply or one call. On also counts a case closed after exactly one completed visit,
            never reopened.
          </p>
        </div>
        <div className="space-y-1">
          <label htmlFor={graceId} className="block text-sm font-medium">
            On-time arrival grace (minutes)
          </label>
          <Input
            id={graceId}
            inputMode="numeric"
            className="h-8 w-24 tabular"
            value={form.arrival_grace_minutes}
            aria-invalid={Boolean(graceError)}
            onChange={(e) => set({ arrival_grace_minutes: e.currentTarget.value })}
          />
          <p className={graceError ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
            {graceError ??
              `A tech or contractor is on time when they check in within this many minutes of the booked start. Default ${DEFAULT_GRACE}.`}
          </p>
        </div>
      </Section>
      <MutationError error={save.error} />
      <div className="flex items-center gap-3">
        <Button
          type="submit"
          variant="primary"
          disabled={!dirty || problems.length > 0 || save.isPending}
          pending={save.isPending}
          disabledReason={save.isPending ? 'Saving' : !dirty ? 'Nothing has changed' : 'Fix the highlighted values first'}
        >
          Save
        </Button>
        {!dirty ? <span className="text-xs text-muted-foreground">No changes</span> : null}
      </div>
    </form>
  )
}

function SettingsBody() {
  const catalogue = useQuery(catalogueQuery())
  const allowed = catalogue.data?.can_edit_settings === true
  const settings = useQuery({ ...settingsQuery(), enabled: allowed })
  if (catalogue.isPending) return <SkeletonRows count={6} />
  if (catalogue.isError || !catalogue.data) {
    return <QueryError error={catalogue.error} what="Report settings" onRetry={() => void catalogue.refetch()} />
  }
  if (!allowed) {
    return <EmptyState title="Only managers can change report settings." detail="Ask a manager if a target or definition looks wrong." />
  }
  if (settings.isPending) return <SkeletonRows count={6} />
  if (settings.isError || !settings.data) {
    return <QueryError error={settings.error} what="Report settings" onRetry={() => void settings.refetch()} />
  }
  const row = settings.data.find((r) => r.key === SETTINGS_KEY)
  if (!row) {
    return (
      <ErrorBanner
        tone="warning"
        title="Report settings unavailable"
        detail="The settings response did not include reports.settings. Nothing is inferred and nothing is saved."
      />
    )
  }
  return <SettingsForm row={row} targetable={catalogue.data.targetable ?? []} />
}

export function ReportsSettingsScreen() {
  return (
    <Frame
      active="reports"
      railless
      main={
        <div className="reports-root flex h-full flex-col overflow-y-auto" data-scroll-top>
          <PaneHeader title={<Breadcrumb title="Settings" />} subtitle="Targets, who sees what, and how two figures are counted." />
          <main className="w-full max-w-3xl px-4 py-4 sm:px-5">
            <SettingsBody />
          </main>
        </div>
      }
    />
  )
}
